import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { LoadingSpinner } from "@/shared/components/ui/loading-spinner";
import { useIsAdmin } from "@/shared/hooks/use-admin-validation";
import { useIsSuperAdminRole } from "@/shared/hooks/use-system-role";
import { useViewScope } from "@/shared/hooks/use-view-scope";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/components/ui/tabs";
import { LearningsPanel } from "@/features/Learnings/components/LearningsPanel";
import { LearningsTabLabel } from "@/features/Learnings/components/LearningsTabLabel";
import { MemoryPanel } from "./MemoryPanel";

/**
 * Memory no My view e na visão da organização (AB#87149). No My view a página é o Brain da
 * pessoa, com as abas Memory e Learnings (AB#87286), e a aba vive no `?tab=`. O nível sai da navegação,
 * como nas Skills; a Memory do workspace vive na aba do Brain.
 */
export const MemoryPage = () => {
  const { t } = useTranslation();
  const { scope, isResolving } = useViewScope();
  const { isAdmin } = useIsAdmin();
  const { isSuperAdmin } = useIsSuperAdminRole();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = ["learning", "learnings"].includes(searchParams.get("tab") ?? "") ? "learnings" : "memory";

  if (isResolving) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (scope === "user") {
    // No próprio espaço a pessoa manda: grava e esquece sem depender de papel. Ao lado, as
    // propostas que dependem dela — as pessoais e as que nasceram das conversas dela (AB#87286).
    return (
      <Tabs
        value={tab}
        className="w-full"
        onValueChange={(value) =>
          setSearchParams(
            (params) => {
              params.set("tab", value);
              return params;
            },
            { replace: true },
          )
        }
      >
        <TabsList>
          <TabsTrigger value="memory">{t("knowledge.brain.tabs.memory")}</TabsTrigger>
          <TabsTrigger value="learnings">
            <LearningsTabLabel target={{ view: "personal" }} />
          </TabsTrigger>
        </TabsList>
        <TabsContent value="memory" className="mt-4">
          <MemoryPanel target={{ scope: "user" }} canCreate />
        </TabsContent>
        <TabsContent value="learnings" className="mt-4">
          <LearningsPanel target={{ view: "personal" }} />
        </TabsContent>
      </Tabs>
    );
  }

  if (scope === "organization") {
    // O Brain da organização: por enquanto só a Memory, na mesma moldura de abas do workspace.
    return (
      <Tabs value="memory" className="w-full">
        <TabsList>
          <TabsTrigger value="memory">{t("knowledge.brain.tabs.memory")}</TabsTrigger>
        </TabsList>
        <TabsContent value="memory" className="mt-4">
          <MemoryPanel target={{ scope: "organization" }} canCreate={isAdmin || isSuperAdmin} />
        </TabsContent>
      </Tabs>
    );
  }

  return (
    <div className="flex min-h-[400px] items-center justify-center text-muted-foreground">
      {t("memory.selectOrganization")}
    </div>
  );
};

export default MemoryPage;
